import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { 
  ShieldCheck, 
  CheckCircle2, 
  AlertCircle, 
  Building2,
  FileText,
  QrCode,
  Settings,
  Globe,
  Key,
  RefreshCw,
  ExternalLink,
  Info,
  Receipt,
  CreditCard,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ZATCASettings = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [configured, setConfigured] = useState(false);
  
  const [settings, setSettings] = useState({
    business_name_ar: '',
    business_name_en: '',
    vat_number: '',
    cr_number: '',
    street: '',
    building_number: '',
    city: 'Riyadh',
    district: '',
    postal_code: '',
    country: 'SA',
    fatoorah_api_key: '',
    fatoorah_environment: 'sandbox',
    auto_submit_invoices: false,
    include_qr_code: true,
    invoice_prefix: 'INV',
    credit_note_prefix: 'CN',
    debit_note_prefix: 'DN',
    is_connected: false,
    last_verified: null
  });

  const [connectionStatus, setConnectionStatus] = useState({
    tested: false,
    success: false,
    message: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/zatca/settings`);
      if (res.data.configured && res.data.settings) {
        setSettings(prev => ({
          ...prev,
          ...res.data.settings,
          fatoorah_api_key: '' // Don't populate API key field
        }));
        setConfigured(true);
        if (res.data.settings.is_connected) {
          setConnectionStatus({
            tested: true,
            success: true,
            message: t('zatca.connectedSuccessfully')
          });
        }
      }
    } catch (err) {
      console.error('Failed to fetch ZATCA settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate required fields
    if (!settings.business_name_ar || !settings.business_name_en) {
      toast.error(t('zatca.enterBusinessNames'));
      return;
    }
    
    if (!settings.vat_number || settings.vat_number.length !== 15) {
      toast.error(t('zatca.vatNumberValidation'));
      return;
    }
    
    if (!settings.vat_number.startsWith('3')) {
      toast.error(t('zatca.vatNumberSaudi'));
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/zatca/settings`, settings);
      toast.success(t('zatca.settingsSaved'));
      setConfigured(true);
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('zatca.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!settings.fatoorah_api_key) {
      toast.error(t('zatca.enterApiKey'));
      return;
    }

    setTesting(true);
    try {
      const res = await axios.post(`${API_URL}/api/zatca/test-connection`, {
        fatoorah_api_key: settings.fatoorah_api_key,
        environment: settings.fatoorah_environment
      });
      
      setConnectionStatus({
        tested: true,
        success: res.data.success,
        message: res.data.message
      });

      if (res.data.success) {
        toast.success(res.data.message);
        // Update connection status in DB
        await axios.post(`${API_URL}/api/zatca/update-connection-status?is_connected=true`);
      } else {
        toast.error(res.data.message);
        await axios.post(`${API_URL}/api/zatca/update-connection-status?is_connected=false&error=${encodeURIComponent(res.data.error)}`);
      }
    } catch (err) {
      setConnectionStatus({
        tested: true,
        success: false,
        message: err.response?.data?.message || t('zatca.connectionFailed')
      });
      toast.error(t('zatca.testConnection'));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isRTL ? 'text-right' : ''}`}>
      {/* Header */}
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t('zatca.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('zatca.subtitle')}
          </p>
        </div>
        <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {configured && (
            <Badge className={connectionStatus.success ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
              {connectionStatus.success ? (
                <><CheckCircle2 className="h-3 w-3 mr-1" /> {t('zatca.connected')}</>
              ) : (
                <><AlertCircle className="h-3 w-3 mr-1" /> {t('zatca.notConnected')}</>
              )}
            </Badge>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4">
          <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className={isRTL ? 'text-right' : ''}>
              <h4 className="font-medium text-blue-900">
                {t('zatca.howToGetApiKey')}
              </h4>
              <p className="text-sm text-blue-700 mt-1">
                {t('zatca.apiKeyInstructions')}
              </p>
              <a 
                href="https://fatoorah.ai" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mt-2 ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                {t('zatca.visitFatoorah')} <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="business" className="space-y-6">
        <TabsList className={`grid grid-cols-3 w-full max-w-md ${isRTL ? 'mr-auto' : ''}`}>
          <TabsTrigger value="business" className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Building2 className="h-4 w-4" /> {t('zatca.business')}
          </TabsTrigger>
          <TabsTrigger value="integration" className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Key className="h-4 w-4" /> {t('zatca.integration')}
          </TabsTrigger>
          <TabsTrigger value="invoicing" className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Receipt className="h-4 w-4" /> {t('zatca.invoicing')}
          </TabsTrigger>
        </TabsList>

        {/* Business Information Tab */}
        <TabsContent value="business">
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Building2 className="h-5 w-5" />
                {t('zatca.businessInformation')}
              </CardTitle>
              <CardDescription>
                {t('zatca.businessInfoDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Business Names */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('zatca.businessNameArabic')}</Label>
                  <Input
                    value={settings.business_name_ar}
                    onChange={e => setSettings({...settings, business_name_ar: e.target.value})}
                    placeholder="شركة أكمي للتجارة"
                    className="text-right"
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('zatca.businessNameEnglish')}</Label>
                  <Input
                    value={settings.business_name_en}
                    onChange={e => setSettings({...settings, business_name_en: e.target.value})}
                    placeholder="Acme Trading Co."
                  />
                </div>
              </div>

              {/* Tax IDs */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    {t('zatca.vatNumber')}
                    <Badge variant="outline" className="text-xs">15 {t('zatca.digits')}</Badge>
                  </Label>
                  <Input
                    value={settings.vat_number}
                    onChange={e => setSettings({...settings, vat_number: e.target.value.replace(/\D/g, '').slice(0, 15)})}
                    placeholder="310123456700003"
                    maxLength={15}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('zatca.vatNumberDesc')}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>{t('zatca.crNumber')}</Label>
                  <Input
                    value={settings.cr_number}
                    onChange={e => setSettings({...settings, cr_number: e.target.value})}
                    placeholder="1010123456"
                  />
                </div>
              </div>

              {/* Address */}
              <div className="space-y-4">
                <h4 className="font-medium">{t('zatca.address')}</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('zatca.street')}</Label>
                    <Input
                      value={settings.street}
                      onChange={e => setSettings({...settings, street: e.target.value})}
                      placeholder={isRTL ? 'شارع الملك فهد' : 'King Fahd Road'}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('zatca.buildingNumber')}</Label>
                    <Input
                      value={settings.building_number}
                      onChange={e => setSettings({...settings, building_number: e.target.value})}
                      placeholder="1234"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>{t('zatca.city')}</Label>
                    <Select value={settings.city} onValueChange={v => setSettings({...settings, city: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Riyadh">{t('zatca.riyadh')}</SelectItem>
                        <SelectItem value="Jeddah">{t('zatca.jeddah')}</SelectItem>
                        <SelectItem value="Dammam">{t('zatca.dammam')}</SelectItem>
                        <SelectItem value="Makkah">{t('zatca.makkah')}</SelectItem>
                        <SelectItem value="Madinah">{t('zatca.madinah')}</SelectItem>
                        <SelectItem value="Khobar">{t('zatca.khobar')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('zatca.district')}</Label>
                    <Input
                      value={settings.district}
                      onChange={e => setSettings({...settings, district: e.target.value})}
                      placeholder={isRTL ? 'العليا' : 'Olaya'}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('zatca.postalCode')}</Label>
                    <Input
                      value={settings.postal_code}
                      onChange={e => setSettings({...settings, postal_code: e.target.value})}
                      placeholder="12345"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integration Tab */}
        <TabsContent value="integration">
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Key className="h-5 w-5" />
                {t('zatca.fatoorahIntegration')}
              </CardTitle>
              <CardDescription>
                {t('zatca.fatoorahIntegrationDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Fatoorah Logo & Info */}
              <div className={`flex items-center gap-4 p-4 bg-muted/50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                <img 
                  src="https://fatoorah.ai/wp-content/uploads/2025/02/%D9%85%D9%86%D8%B5%D8%A9-%D9%81%D8%A7%D8%AA%D9%88%D8%B1%D8%A9.png" 
                  alt="Fatoorah" 
                  className="h-10 w-auto"
                />
                <div className={isRTL ? 'text-right' : ''}>
                  <h4 className="font-medium">{t('zatca.fatoorahPlatform')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('zatca.fatoorahPartner')}
                  </p>
                </div>
              </div>

              {/* API Key */}
              <div className="space-y-2">
                <Label>{t('zatca.apiKey')}</Label>
                <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Input
                    type="password"
                    value={settings.fatoorah_api_key}
                    onChange={e => setSettings({...settings, fatoorah_api_key: e.target.value})}
                    placeholder={settings.fatoorah_api_key_masked || 'fat_live_xxxxxxxxxxxx'}
                    className="flex-1"
                  />
                  <Button 
                    variant="outline" 
                    onClick={handleTestConnection}
                    disabled={testing || !settings.fatoorah_api_key}
                  >
                    {testing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    <span className={isRTL ? 'mr-2' : 'ml-2'}>
                      {t('zatca.test')}
                    </span>
                  </Button>
                </div>
                {connectionStatus.tested && (
                  <div className={`flex items-center gap-2 text-sm ${connectionStatus.success ? 'text-emerald-600' : 'text-red-600'} ${isRTL ? 'flex-row-reverse' : ''}`}>
                    {connectionStatus.success ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                    {connectionStatus.message}
                  </div>
                )}
              </div>

              {/* Environment */}
              <div className="space-y-2">
                <Label>{t('zatca.environment')}</Label>
                <Select value={settings.fatoorah_environment} onValueChange={v => setSettings({...settings, fatoorah_environment: v})}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">
                      <span className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        🧪 {t('zatca.sandbox')}
                      </span>
                    </SelectItem>
                    <SelectItem value="production">
                      <span className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        🚀 {t('zatca.production')}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t('zatca.useSandboxFirst')}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoicing Tab */}
        <TabsContent value="invoicing">
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Receipt className="h-5 w-5" />
                {t('zatca.invoiceSettings')}
              </CardTitle>
              <CardDescription>
                {t('zatca.invoiceSettingsDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Prefixes */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('zatca.invoicePrefix')}</Label>
                  <Input
                    value={settings.invoice_prefix}
                    onChange={e => setSettings({...settings, invoice_prefix: e.target.value.toUpperCase()})}
                    placeholder="INV"
                    maxLength={5}
                  />
                  <p className="text-xs text-muted-foreground">e.g. INV-000001</p>
                </div>
                <div className="space-y-2">
                  <Label>{t('zatca.creditNotePrefix')}</Label>
                  <Input
                    value={settings.credit_note_prefix}
                    onChange={e => setSettings({...settings, credit_note_prefix: e.target.value.toUpperCase()})}
                    placeholder="CN"
                    maxLength={5}
                  />
                  <p className="text-xs text-muted-foreground">e.g. CN-000001</p>
                </div>
                <div className="space-y-2">
                  <Label>{t('zatca.debitNotePrefix')}</Label>
                  <Input
                    value={settings.debit_note_prefix}
                    onChange={e => setSettings({...settings, debit_note_prefix: e.target.value.toUpperCase()})}
                    placeholder="DN"
                    maxLength={5}
                  />
                  <p className="text-xs text-muted-foreground">e.g. DN-000001</p>
                </div>
              </div>

              {/* Toggles */}
              <div className="space-y-4">
                <div className={`flex items-center justify-between p-4 bg-muted/50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={isRTL ? 'text-right' : ''}>
                    <Label className="text-base">{t('zatca.autoSubmitInvoices')}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t('zatca.autoSubmitDesc')}
                    </p>
                  </div>
                  <Switch
                    checked={settings.auto_submit_invoices}
                    onCheckedChange={v => setSettings({...settings, auto_submit_invoices: v})}
                  />
                </div>

                <div className={`flex items-center justify-between p-4 bg-muted/50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={isRTL ? 'text-right' : ''}>
                    <Label className="text-base">{t('zatca.includeQrCode')}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t('zatca.includeQrCodeDesc')}
                    </p>
                  </div>
                  <Switch
                    checked={settings.include_qr_code}
                    onCheckedChange={v => setSettings({...settings, include_qr_code: v})}
                  />
                </div>
              </div>

              {/* QR Code Preview */}
              <div className={`flex items-center gap-4 p-4 border rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="h-24 w-24 bg-muted rounded-lg flex items-center justify-center">
                  <QrCode className="h-12 w-12 text-muted-foreground" />
                </div>
                <div className={isRTL ? 'text-right' : ''}>
                  <h4 className="font-medium">{t('zatca.qrCodePreview')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('zatca.qrCodeDesc')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save Button */}
      <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : 'justify-end'}`}>
        <Button variant="outline" onClick={fetchSettings}>
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className={`h-4 w-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />}
          {t('zatca.saveSettings')}
        </Button>
      </div>
    </div>
  );
};

export default ZATCASettings;
